import { Transition } from '@headlessui/react'
import { AccountType } from '@/common/types'
import { ONBOARDING_COPY } from '@/locales/en'
import { LabelInputRadio } from '@/common/ui/LabelInputRadio'
import { useEffect, useCallback } from 'react'

type Props = {
    selectedAccountType: AccountType | null
    setSelectedAccountType: (t: AccountType | null) => void
    isTrust: boolean
    setIsTrust: (b: boolean) => void
    supportedAccountTypes: AccountType[]
    showDisabledTrustOption?: boolean
    showDisabledCompanyOption?: boolean
    disabledOptionBadgeText?: string
    useThirdPersonCopy?: boolean
}

export default function AccountTypeSelector({
    selectedAccountType,
    setSelectedAccountType,
    isTrust,
    setIsTrust,
    supportedAccountTypes,
    showDisabledTrustOption = false,
    showDisabledCompanyOption = false,
    disabledOptionBadgeText,
    useThirdPersonCopy = false,
}: Props) {
    const isSupported = useCallback((t: AccountType) => supportedAccountTypes.includes(t), [supportedAccountTypes])

    const hasTrustOptions = isSupported(AccountType.INDIVIDUAL_TRUST) || isSupported(AccountType.COMPANY_TRUST)
    const shouldRenderTrustOption = hasTrustOptions || showDisabledTrustOption
    const isTrustOptionDisabled = !hasTrustOptions && showDisabledTrustOption
    const shouldRenderCompanyOption = isSupported(AccountType.COMPANY) || showDisabledCompanyOption
    const isCompanyOptionDisabled = !isSupported(AccountType.COMPANY) && showDisabledCompanyOption

    useEffect(() => {
        const onlyTrusts =
            supportedAccountTypes.length === 2 &&
            isSupported(AccountType.INDIVIDUAL_TRUST) &&
            isSupported(AccountType.COMPANY_TRUST)
        if (onlyTrusts) {
            setIsTrust(true)
        }
    }, [supportedAccountTypes, isSupported, setIsTrust])

    useEffect(() => {
        if (supportedAccountTypes.length !== 1) {
            return
        }

        const onlyType = supportedAccountTypes[0]
        const isOnlyTypeTrust = onlyType === AccountType.INDIVIDUAL_TRUST || onlyType === AccountType.COMPANY_TRUST

        setIsTrust(isOnlyTypeTrust)

        if (selectedAccountType !== onlyType) {
            setSelectedAccountType(onlyType)
        }
    }, [selectedAccountType, setIsTrust, setSelectedAccountType, supportedAccountTypes])

    const normalizeDescription = (description: string) =>
        useThirdPersonCopy ? description.replace(/\byour\b/gi, 'their') : description

    const handleAccountTypeChange = (value: string | AccountType) => {
        if (value === 'trustOrSelfManagedSuperFund') {
            setSelectedAccountType(null)
            setIsTrust(true)
        } else {
            setIsTrust(false)
            setSelectedAccountType(value as AccountType)
        }
    }

    return (
        <div className="flex flex-col gap-4 max-w-[520px]">
            {isSupported(AccountType.INDIVIDUAL) && (
                <LabelInputRadio
                    type="radio"
                    title={ONBOARDING_COPY.accountType.options.individual.label}
                    description={normalizeDescription(ONBOARDING_COPY.accountType.options.individual.description)}
                    id="accountType-individual"
                    name="accountType"
                    checked={!isTrust && selectedAccountType === AccountType.INDIVIDUAL}
                    onChange={() => handleAccountTypeChange(AccountType.INDIVIDUAL)}
                    required
                />
            )}

            {shouldRenderTrustOption && (
                <LabelInputRadio
                    type="radio"
                    title={ONBOARDING_COPY.accountType.options.trustOrSelfManagedSuperFund.label}
                    description={normalizeDescription(
                        ONBOARDING_COPY.accountType.options.trustOrSelfManagedSuperFund.description,
                    )}
                    id="accountType-trustOrSelfManagedSuperFund"
                    name="accountType"
                    checked={isTrust}
                    onChange={() => handleAccountTypeChange('trustOrSelfManagedSuperFund')}
                    disabled={isTrustOptionDisabled}
                    badgeText={isTrustOptionDisabled ? disabledOptionBadgeText : undefined}
                    required
                />
            )}

            <Transition
                show={isTrust}
                enter="transition-all duration-300"
                enterFrom="opacity-0 max-h-0 -translate-y-2"
                enterTo="opacity-100 max-h-[200px] translate-y-0"
                leave="transition-all duration-200"
                leaveFrom="opacity-100 max-h-[200px] translate-y-0"
                leaveTo="opacity-0 max-h-0 -translate-y-2"
            >
                <div className="flex flex-col gap-4 pl-8 w-full overflow-hidden">
                    {isSupported(AccountType.INDIVIDUAL_TRUST) && (
                        <LabelInputRadio
                            type="radio"
                            title={ONBOARDING_COPY.accountType.options.individualTrust.label}
                            description={normalizeDescription(
                                ONBOARDING_COPY.accountType.options.individualTrust.description,
                            )}
                            id="accountType-individualTrust"
                            name="accountType"
                            checked={selectedAccountType === AccountType.INDIVIDUAL_TRUST}
                            onChange={() => setSelectedAccountType(AccountType.INDIVIDUAL_TRUST)}
                            required
                            className="w-full"
                        />
                    )}

                    {isSupported(AccountType.COMPANY_TRUST) && (
                        <LabelInputRadio
                            type="radio"
                            title={ONBOARDING_COPY.accountType.options.companyTrust.label}
                            description={normalizeDescription(
                                ONBOARDING_COPY.accountType.options.companyTrust.description,
                            )}
                            id="accountType-companyTrust"
                            name="accountType"
                            checked={selectedAccountType === AccountType.COMPANY_TRUST}
                            onChange={() => setSelectedAccountType(AccountType.COMPANY_TRUST)}
                            required
                            className="w-full"
                        />
                    )}
                </div>
            </Transition>

            {shouldRenderCompanyOption && (
                <LabelInputRadio
                    type="radio"
                    title={ONBOARDING_COPY.accountType.options.company.label}
                    description={normalizeDescription(ONBOARDING_COPY.accountType.options.company.description)}
                    id="accountType-company"
                    name="accountType"
                    checked={!isTrust && selectedAccountType === AccountType.COMPANY}
                    onChange={() => handleAccountTypeChange(AccountType.COMPANY)}
                    disabled={isCompanyOptionDisabled}
                    badgeText={isCompanyOptionDisabled ? disabledOptionBadgeText : undefined}
                    required
                />
            )}
        </div>
    )
}
